/**
 * Thin typed wrapper around the `window.offlineApi` bridge exposed by
 * frontend/desktop/src/preload.ts. Keep this shape in sync with that
 * file's `offlineApi` object -- it's the IPC contract between the two
 * separately-built packages.
 *
 * Outside the Electron shell (e.g. running the plain web app in a
 * browser), `window.offlineApi` is undefined and every call rejects --
 * callers must go through isDesktopRuntime()/useOnlineStatus rather than
 * assuming this module is always usable.
 */

export interface EnqueueWriteInput {
  entityType: string;
  handler: string;
  payload: unknown;
  dependsOn?: string[];
  batchId: string;
  patientUuid?: string | null;
  clientUuid?: string;
}

export interface WriteQueueRow {
  id: number;
  client_uuid: string;
  entity_type: string;
  handler: string;
  payload: string;
  depends_on: string;
  batch_id: string;
  status: 'pending' | 'in-flight' | 'done' | 'failed';
  attempt_count: number;
  last_error: string | null;
  next_retry_at: string | null;
  created_at: string;
  patient_uuid: string | null;
}

export interface SyncFailureRow {
  id: number;
  client_uuid: string;
  entity_type: string;
  handler: string;
  error: string;
  attempt_number: number;
  occurred_at: string;
}

export interface CachedPatient {
  uuid: string;
  fhir_patient_id: string | null;
  clinic_id: number | null;
  professional_id: number | null;
  patient_name: string;
  patient_identifier: string;
  patient_birth_date: string | null;
  patient_gender: string | null;
  patient_phone: string;
  patient_email: string;
  synced: 0 | 1;
  updated_at: string;
}

export interface UpsertCachedPatientInput {
  uuid: string;
  fhir_patient_id?: string | null;
  clinic_id?: number | null;
  professional_id?: number | null;
  patient_name: string;
  patient_identifier?: string;
  patient_birth_date?: string | null;
  patient_gender?: string | null;
  patient_phone?: string;
  patient_email?: string;
  synced: boolean;
}

interface OfflineApi {
  enqueueWrite: (input: EnqueueWriteInput) => Promise<WriteQueueRow>;
  listPendingWrites: () => Promise<WriteQueueRow[]>;
  listAllWrites: () => Promise<WriteQueueRow[]>;
  updateWriteStatus: (params: {
    clientUuid: string;
    status: string;
    lastError?: string | null;
    nextRetryAt?: string | null;
  }) => Promise<void>;
  recordSyncFailure: (params: {
    clientUuid: string;
    entityType: string;
    handler: string;
    error: string;
    attemptNumber: number;
  }) => Promise<void>;
  getSyncFailureHistory: (clientUuid: string) => Promise<SyncFailureRow[]>;
  upsertCachedPatients: (patients: UpsertCachedPatientInput[]) => Promise<void>;
  listCachedPatients: (params?: {
    clinicId?: number;
    professionalId?: number;
    search?: string;
  }) => Promise<CachedPatient[]>;
  getCachedPatient: (uuid: string) => Promise<CachedPatient | undefined>;
  markPatientSynced: (uuid: string) => Promise<void>;
  setScheduleCache: (key: string, data: unknown) => Promise<void>;
  getScheduleCache: (key: string) => Promise<unknown>;
  printHtml: (html: string) => Promise<void>;
  transcribeAudioNative: (audioBuffer: ArrayBuffer) => Promise<{ transcript: string }>;
}

declare global {
  interface Window {
    offlineApi?: OfflineApi;
  }
}

export function isDesktopRuntime(): boolean {
  return typeof window !== 'undefined' && window.offlineApi !== undefined;
}

function requireOfflineApi(): OfflineApi {
  if (!window.offlineApi) {
    throw new Error(
      'offlineApi is unavailable -- this is only usable inside the Electron desktop shell.'
    );
  }
  return window.offlineApi;
}

export function enqueueWrite(input: EnqueueWriteInput): Promise<WriteQueueRow> {
  return requireOfflineApi().enqueueWrite(input);
}

export function listPendingWrites(): Promise<WriteQueueRow[]> {
  return requireOfflineApi().listPendingWrites();
}

export function listAllWrites(): Promise<WriteQueueRow[]> {
  return requireOfflineApi().listAllWrites();
}

export function updateWriteStatus(params: {
  clientUuid: string;
  status: string;
  lastError?: string | null;
  nextRetryAt?: string | null;
}): Promise<void> {
  return requireOfflineApi().updateWriteStatus(params);
}

export function recordSyncFailure(params: {
  clientUuid: string;
  entityType: string;
  handler: string;
  error: string;
  attemptNumber: number;
}): Promise<void> {
  return requireOfflineApi().recordSyncFailure(params);
}

export function getSyncFailureHistory(clientUuid: string): Promise<SyncFailureRow[]> {
  return requireOfflineApi().getSyncFailureHistory(clientUuid);
}

export function upsertCachedPatients(patients: UpsertCachedPatientInput[]): Promise<void> {
  return requireOfflineApi().upsertCachedPatients(patients);
}

export function listCachedPatients(params?: {
  clinicId?: number;
  professionalId?: number;
  search?: string;
}): Promise<CachedPatient[]> {
  return requireOfflineApi().listCachedPatients(params);
}

export function getCachedPatient(uuid: string): Promise<CachedPatient | undefined> {
  return requireOfflineApi().getCachedPatient(uuid);
}

export function markPatientSynced(uuid: string): Promise<void> {
  return requireOfflineApi().markPatientSynced(uuid);
}

export function setScheduleCache(key: string, data: unknown): Promise<void> {
  return requireOfflineApi().setScheduleCache(key, data);
}

export function getScheduleCache<T = unknown>(key: string): Promise<T | undefined> {
  return requireOfflineApi().getScheduleCache(key) as Promise<T | undefined>;
}

export function printHtml(html: string): Promise<void> {
  return requireOfflineApi().printHtml(html);
}

export function transcribeAudioNative(audioBuffer: ArrayBuffer): Promise<{ transcript: string }> {
  return requireOfflineApi().transcribeAudioNative(audioBuffer);
}
